import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { inventoryAPI, programsAPI, purchaseAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SearchableSelect } from '@/components/SearchableSelect';
import { ItemRowThumb } from '@/components/AttachmentThumb';
import { Plus, Search, Package, Wrench, ChevronLeft, ChevronRight, Copy, Files, X, Trash2, Ban, CheckCircle, Link2, Download, Upload, Loader2, FileSpreadsheet, ArrowUp, ArrowDown, ChevronsUpDown, SlidersHorizontal, Ruler } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { DuplicateSheetDialog } from '@/components/DuplicateSheetDialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const ITEM_TYPES = ['Raw Material', 'Service', 'Consumable', 'Semi-Finished Part', 'Finished Good', 'Scrap', 'Job Work Item'];
const TYPE_COLORS = {
  'Fixed Asset': 'bg-blue-50 text-blue-700 border-blue-200',
  'Raw Material': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Service': 'bg-purple-50 text-purple-700 border-purple-200',
  'Consumable': 'bg-amber-50 text-amber-700 border-amber-200',
  'Semi-Finished Part': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  'Finished Good': 'bg-teal-50 text-teal-700 border-teal-200',
  'Scrap': 'bg-slate-50 text-slate-600 border-slate-200',
  'Job Work Item': 'bg-orange-50 text-orange-700 border-orange-200',
};

function SortableTh({ label, field, sortBy, sortOrder, onSort, align = 'left', className = '' }) {
  const active = sortBy === field;
  const Icon = !active ? ChevronsUpDown : (sortOrder === 'asc' ? ArrowUp : ArrowDown);
  return (
    <th className={`p-2.5 text-xs font-medium text-muted-foreground ${align === 'right' ? 'text-right' : 'text-left'} ${className}`}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${active ? 'text-foreground font-semibold' : ''}`}
        data-testid={`sort-${field}`}
      >
        {label}
        <Icon className={`w-3 h-3 ${active ? 'text-amber-600' : 'opacity-50'}`} />
      </button>
    </th>
  );
}

function ItemTableHeader({ items, selectedIds, onSelectAll, sortBy, sortOrder, onSort }) {
  const allSelected = items.length > 0 && items.every(item => selectedIds.includes(item.id));
  const someSelected = items.some(item => selectedIds.includes(item.id)) && !allSelected;
  
  return (
    <tr className="border-b bg-muted/50">
      <th className="p-2.5 w-10">
        <Checkbox
          checked={allSelected || (someSelected ? "indeterminate" : false)}
          onCheckedChange={() => onSelectAll(items)}
          data-testid="select-all-checkbox"
        />
      </th>
      <SortableTh label="Unique ID" field="unique_id" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
      <SortableTh label="Item Name" field="item_name" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
      <SortableTh label="Type" field="item_type" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
      <SortableTh label="Make/Buy" field="make_or_buy" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
      <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">UOM</th>
      <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Inventory</th>
      <th className="text-right p-2.5 text-xs font-medium text-muted-foreground">Available Stock</th>
      <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Procurement</th>
      <SortableTh label="Category" field="category" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
      <th className="text-left p-2.5 text-xs font-medium text-muted-foreground w-12">Actions</th>
    </tr>
  );
}

const PROCUREMENT_BADGE_STYLE = {
  indent: 'bg-slate-50 text-slate-700 border-slate-300',
  pending_quote: 'bg-amber-50 text-amber-700 border-amber-300',
  quoted: 'bg-amber-50 text-amber-700 border-amber-300',
  pr: 'bg-blue-50 text-blue-700 border-blue-300',
  approved: 'bg-blue-50 text-blue-700 border-blue-300',
  ordered: 'bg-purple-50 text-purple-700 border-purple-300',
  partially_received: 'bg-orange-50 text-orange-700 border-orange-300',
};

function ProcurementBadge({ status, item, navigate }) {
  if (!status) return <span className="text-[10px] text-muted-foreground">-</span>;
  const cls = PROCUREMENT_BADGE_STYLE[status.primary] || 'bg-slate-50 text-slate-700 border-slate-300';
  const tooltipParts = [];
  if (status.po_numbers?.length) tooltipParts.push(`PO(s): ${status.po_numbers.join(', ')}`);
  if (status.ordered_qty) tooltipParts.push(`Ordered: ${status.ordered_qty}`);
  if (status.received_qty) tooltipParts.push(`Received: ${status.received_qty}`);
  if (status.pending_qty) tooltipParts.push(`Pending: ${status.pending_qty}`);
  tooltipParts.push('Click to open related PR/PO');

  const handleClick = (e) => {
    e.stopPropagation();
    const itemCode = item?.unique_id || item?.item_name || '';
    if (status.primary === 'ordered' || status.primary === 'partially_received') {
      // Jump to PR list filtered to this item's PO. Use first PO number if available, else item code.
      const q = (status.po_numbers && status.po_numbers[0]) || itemCode;
      const targetStatus = status.primary === 'partially_received' ? 'partially_received' : 'po_sent';
      navigate(`/purchase/requests?status=${targetStatus}&q=${encodeURIComponent(q)}`);
    } else if (status.primary === 'approved' || status.primary === 'pr') {
      const targetStatus = status.primary === 'approved' ? 'approved' : 'pending_approval';
      navigate(`/purchase/requests?status=${targetStatus}&q=${encodeURIComponent(itemCode)}`);
    } else {
      // Pre-PR phase — go to indent page
      navigate('/purchase/indent');
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`text-[10px] inline-block px-1.5 py-0.5 rounded border cursor-pointer hover:brightness-95 transition ${cls}`}
      title={tooltipParts.join('\n')}
      data-testid={`procurement-badge-${status.item_id}`}
    >
      {status.label}
    </button>
  );
}

function ItemTableBody({ items, loading, navigate, selectedIds, onSelectItem, onDeleteItem, onSuspendItem, onLinkProgram, onDuplicateSheet, isAdmin, procurementStatuses }) {
  if (loading) return <tr><td colSpan={12} className="text-center py-8 text-xs text-muted-foreground">Loading...</td></tr>;
  if (items.length === 0) return <tr><td colSpan={12} className="text-center py-8 text-xs text-muted-foreground">No items found</td></tr>;
  
  const handleCopy = (e, itemId) => {
    e.stopPropagation();
    navigate(`/inventory/items/new?copyFrom=${itemId}`);
  };

  const handleCheckboxChange = (itemId) => {
    onSelectItem(itemId);
  };

  const handleRowClick = (e, item) => {
    // If clicking on checkbox cell, don't navigate
    if (e.target.closest('[data-checkbox-cell]')) {
      return;
    }
    navigate(`/inventory/items/${item.id}`);
  };

  const isSuspended = (item) => item.status === 'suspended';
  const isPendingApproval = (item) => item.status === 'pending_approval';
  const isRejected = (item) => item.status === 'rejected';
  
  return (
    <>
      {items.map(item => (
        <tr key={item.id} className={`border-b hover:bg-muted/50 transition-colors cursor-pointer ${selectedIds.includes(item.id) ? 'bg-blue-50' : ''} ${isSuspended(item) ? 'opacity-60 bg-gray-50' : ''} ${isPendingApproval(item) ? 'bg-amber-50/50' : ''} ${isRejected(item) ? 'bg-red-50/50 opacity-60' : ''}`}
          onClick={(e) => handleRowClick(e, item)} data-testid={`item-row-${item.unique_id || item.id}`}>
          <td className="p-2.5" data-checkbox-cell="true">
            <Checkbox
              checked={selectedIds.includes(item.id)}
              onCheckedChange={() => handleCheckboxChange(item.id)}
              data-testid={`select-item-${item.unique_id || item.id}`}
            />
          </td>
          <td className="p-2.5 font-mono text-xs font-medium">
            <div className="flex items-center gap-1.5 flex-wrap">
              {item.unique_id || <span className="text-muted-foreground italic">No ID</span>}
              {isSuspended(item) && (
                <Badge variant="outline" className="text-[9px] bg-gray-100 text-gray-600 border-gray-300">
                  Suspended
                </Badge>
              )}
              {isPendingApproval(item) && (
                <Badge variant="outline" className="text-[9px] bg-amber-100 text-amber-700 border-amber-300">
                  Pending Approval
                </Badge>
              )}
              {isRejected(item) && (
                <Badge variant="outline" className="text-[9px] bg-red-100 text-red-700 border-red-300">
                  Rejected
                </Badge>
              )}
            </div>
          </td>
          <td className="p-2.5">
            <div className="flex items-center gap-2">
              <ItemRowThumb itemId={item.id} primaryAttachment={item.primary_attachment} count={item.attachment_count} onClick={() => navigate(`/inventory/items/${item.id}`)} />
              <div>
                <p className={`text-xs font-medium ${isSuspended(item) || isRejected(item) ? 'line-through text-muted-foreground' : ''}`}>{item.item_name}</p>
                {item.external_id && <p className="text-[10px] text-muted-foreground">Ext: {item.external_id}</p>}
              </div>
            </div>
          </td>
          <td className="p-2.5">
            <Badge variant="outline" className={`text-[10px] ${TYPE_COLORS[item.item_type] || ''}`}>
              {item.item_type}
            </Badge>
          </td>
          <td className="p-2.5 text-xs capitalize">{item.make_or_buy}</td>
          <td className="p-2.5 text-xs">{item.unit_of_measure}</td>
          <td className="p-2.5">
            <Badge variant={item.is_inventory_item ? "default" : "secondary"} className="text-[10px]">
              {item.is_inventory_item ? 'Yes' : 'No'}
            </Badge>
          </td>
          <td className="p-2.5 text-right">
            {item.is_inventory_item ? (
              <div data-testid={`item-stock-${item.unique_id || item.id}`}>
                <span className={`text-xs font-mono font-medium ${(item.available_stock || 0) > 0 ? 'text-emerald-600' : (item.available_stock || 0) < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                  {item.available_stock != null ? item.available_stock : '-'}
                </span>
                {(item.committed_stock || 0) > 0 && (
                  <span className="block text-[9px] text-amber-600 font-mono">WO: {item.committed_stock}</span>
                )}
              </div>
            ) : (
              <span className="text-xs text-muted-foreground">-</span>
            )}
          </td>
          <td className="p-2.5">
            <ProcurementBadge status={procurementStatuses?.[item.id]} item={item} navigate={navigate} />
          </td>
          <td className="p-2.5 text-xs text-muted-foreground">{item.category || '-'}</td>
          <td className="p-2.5">
            <div className="flex gap-0.5">
              <Button 
                size="sm" 
                variant="ghost" 
                className="h-7 w-7 p-0 text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50" 
                onClick={(e) => { e.stopPropagation(); onLinkProgram(item); }}
                title="Link to Program"
                data-testid={`link-program-${item.unique_id || item.id}`}
              >
                <Link2 className="w-3.5 h-3.5" />
              </Button>
              <Button 
                size="sm" 
                variant="ghost" 
                className="h-7 w-7 p-0" 
                onClick={(e) => handleCopy(e, item.id)}
                title="Copy Item"
                data-testid={`copy-item-${item.unique_id || item.id}`}
              >
                <Copy className="w-3.5 h-3.5" />
              </Button>
              {onDuplicateSheet && item.item_type === 'Raw Material' && (item.sub_category === 'Sheet Metal' || item.sheet_length || item.sheet_width) && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0 text-amber-600 hover:text-amber-800 hover:bg-amber-50"
                  onClick={(e) => { e.stopPropagation(); onDuplicateSheet(item); }}
                  title="Duplicate to new sheet size"
                  data-testid={`duplicate-sheet-${item.unique_id || item.id}`}
                >
                  <Ruler className="w-3.5 h-3.5" />
                </Button>
              )}
              {isAdmin && item.status !== 'pending_approval' && item.status !== 'rejected' && (
                <Button
                  size="sm"
                  variant="ghost"
                  className={`h-7 w-7 p-0 ${isSuspended(item) ? 'text-green-500 hover:text-green-700 hover:bg-green-50' : 'text-amber-500 hover:text-amber-700 hover:bg-amber-50'}`}
                  onClick={(e) => { e.stopPropagation(); onSuspendItem(item); }}
                  title={isSuspended(item) ? "Reactivate Item" : "Suspend Item"}
                  data-testid={`suspend-item-${item.unique_id || item.id}`}
                >
                  {isSuspended(item) ? <CheckCircle className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                onClick={(e) => { e.stopPropagation(); onDeleteItem(item); }}
                title="Delete Item"
                data-testid={`delete-item-${item.unique_id || item.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </td>
        </tr>
      ))}
    </>
  );
}

function Pagination({ page, totalPages, total, setPage, label }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between p-3 border-t">
      <p className="text-xs text-muted-foreground">Page {page} of {totalPages} ({total} {label})</p>
      <div className="flex gap-1">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs">
          <ChevronLeft className="w-3 h-3 mr-1" /> Prev
        </Button>
        <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 text-xs">
          Next <ChevronRight className="w-3 h-3 ml-1" />
        </Button>
      </div>
    </div>
  );
}

export default function ItemMasterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const initialTab = searchParams.get('type') === 'Fixed Asset' ? 'assets' : 'items';

  const [activeTab, setActiveTab] = useState(initialTab);

  // Items tab state
  const [items, setItems] = useState([]);
  const [dupSheetItem, setDupSheetItem] = useState(null);
  const [procurementStatuses, setProcurementStatuses] = useState({});
  const [itemsTotal, setItemsTotal] = useState(0);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [itemsSearch, setItemsSearch] = useState('');
  const [itemsTypeFilter, setItemsTypeFilter] = useState(searchParams.get('type') && searchParams.get('type') !== 'Fixed Asset' ? searchParams.get('type') : '');
  const [itemsMakeOrBuy, setItemsMakeOrBuy] = useState('');
  const [itemsPage, setItemsPage] = useState(1);
  const [itemsSortBy, setItemsSortBy] = useState('created_at');
  const [itemsSortOrder, setItemsSortOrder] = useState('desc');
  // Advanced filter (applied instantly on Apply)
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advExternalId, setAdvExternalId] = useState('');
  const [advCategory, setAdvCategory] = useState('');
  const [advInventory, setAdvInventory] = useState(''); // '', 'yes', 'no'
  const [appliedAdv, setAppliedAdv] = useState({ external_id: '', category: '', inventory: '' });

  // Fixed Assets tab state
  const [assets, setAssets] = useState([]);
  const [assetsTotal, setAssetsTotal] = useState(0);
  const [assetsLoading, setAssetsLoading] = useState(true);
  const [assetsSearch, setAssetsSearch] = useState('');
  const [assetsPage, setAssetsPage] = useState(1);

  // Multi-select state
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [selectedAssetIds, setSelectedAssetIds] = useState([]);
  
  // Bulk copy dialog
  const [bulkCopyOpen, setBulkCopyOpen] = useState(false);
  const [bulkCopyLoading, setBulkCopyLoading] = useState(false);
  const [bulkCopyType, setBulkCopyType] = useState('items'); // 'items' or 'assets'
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [linkTarget, setLinkTarget] = useState(null);
  const [linkProgramList, setLinkProgramList] = useState([]);
  const [linkForm, setLinkForm] = useState({ program_id: '', item_type: 'purchase', quantity: 0, unit_price: 0 });
  const [linkSaving, setLinkSaving] = useState(false);

  // Excel template download + bulk import
  const importInputRef = useRef(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [tidying, setTidying] = useState(false);

  const handleTidyCategories = async () => {
    setTidying(true);
    try {
      const { data } = await inventoryAPI.cleanupCategories();
      if (data.updated > 0) {
        toast.success(`Tidied ${data.updated} of ${data.scanned} item categorie(s) to standard labels`);
        loadItems();
      } else {
        toast.info(`All ${data.scanned} categorised item(s) already use standard labels`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Category cleanup failed');
    } finally {
      setTidying(false);
    }
  };

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const res = await inventoryAPI.downloadItemTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'item_master_template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Could not download template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (importInputRef.current) importInputRef.current.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await inventoryAPI.importItemsExcel(formData);
      const data = res.data;
      setImportResult(data);
      if (data.created > 0) {
        toast.success(`Imported ${data.created} item(s)${data.skipped ? `, skipped ${data.skipped} duplicate(s)` : ''}`);
        loadItems();
        loadAssets();
      } else if (data.skipped > 0 && data.error_count === 0) {
        toast.info(`No new items — ${data.skipped} duplicate(s) skipped`);
      } else if (data.error_count > 0) {
        toast.error(`Import finished with ${data.error_count} error(s)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const limit = 25;

  useEffect(() => { loadItems(); }, [itemsPage, itemsTypeFilter, itemsMakeOrBuy, itemsSortBy, itemsSortOrder, appliedAdv]);
  useEffect(() => { loadAssets(); }, [assetsPage]);

  // Clear selections when tab changes
  useEffect(() => {
    setSelectedItemIds([]);
    setSelectedAssetIds([]);
  }, [activeTab]);

  const loadItems = async () => {
    setItemsLoading(true);
    try {
      const params = { page: itemsPage, limit, sort_by: itemsSortBy, sort_order: itemsSortOrder, exclude_type: 'Fixed Asset', include_suspended: true };
      if (itemsSearch) params.search = itemsSearch;
      if (itemsTypeFilter) params.item_type = itemsTypeFilter;
      if (itemsMakeOrBuy) params.make_or_buy = itemsMakeOrBuy;
      if (appliedAdv.external_id) params.external_id = appliedAdv.external_id;
      if (appliedAdv.category) params.category = appliedAdv.category;
      if (appliedAdv.inventory === 'yes') params.is_inventory = true;
      if (appliedAdv.inventory === 'no') params.is_inventory = false;
      const res = await inventoryAPI.listItems(params);
      const fetched = res.data.items || [];
      setItems(fetched);
      setItemsTotal(res.data.total);
      // Batch-fetch procurement status for visible items (best-effort, non-blocking)
      const ids = fetched.map(it => it.id).filter(Boolean);
      if (ids.length) {
        try {
          const ps = await purchaseAPI.getItemsProcurementStatus(ids);
          setProcurementStatuses(ps.data?.statuses || {});
        } catch {
          setProcurementStatuses({});
        }
      } else {
        setProcurementStatuses({});
      }
    } catch { toast.error('Failed to load items'); }
    finally { setItemsLoading(false); }
  };

  const loadAssets = async () => {
    setAssetsLoading(true);
    try {
      const params = { page: assetsPage, limit, sort_by: 'created_at', sort_order: 'desc', item_type: 'Fixed Asset', include_suspended: true };
      if (assetsSearch) params.search = assetsSearch;
      const res = await inventoryAPI.listItems(params);
      setAssets(res.data.items);
      setAssetsTotal(res.data.total);
    } catch { toast.error('Failed to load assets'); }
    finally { setAssetsLoading(false); }
  };

  const handleItemsSearch = (e) => { e.preventDefault(); setItemsPage(1); loadItems(); };
  const handleAssetsSearch = (e) => { e.preventDefault(); setAssetsPage(1); loadAssets(); };

  const handleSort = (field) => {
    setItemsPage(1);
    if (itemsSortBy === field) {
      setItemsSortOrder(o => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setItemsSortBy(field);
      setItemsSortOrder('asc');
    }
  };

  const applyAdvancedFilter = () => {
    setItemsPage(1);
    setAppliedAdv({ external_id: advExternalId.trim(), category: advCategory.trim(), inventory: advInventory });
  };
  const clearAdvancedFilter = () => {
    setAdvExternalId(''); setAdvCategory(''); setAdvInventory('');
    setItemsPage(1);
    setAppliedAdv({ external_id: '', category: '', inventory: '' });
  };
  const activeAdvCount = [appliedAdv.external_id, appliedAdv.category, appliedAdv.inventory].filter(Boolean).length;

  // Selection handlers for items
  const handleSelectItem = (itemId) => {
    setSelectedItemIds(prev => 
      prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
    );
  };

  const handleSelectAllItems = (currentItems) => {
    const currentIds = currentItems.map(item => item.id);
    const allSelected = currentIds.every(id => selectedItemIds.includes(id));
    if (allSelected) {
      setSelectedItemIds(prev => prev.filter(id => !currentIds.includes(id)));
    } else {
      setSelectedItemIds(prev => [...new Set([...prev, ...currentIds])]);
    }
  };

  // Selection handlers for assets
  const handleSelectAsset = (assetId) => {
    setSelectedAssetIds(prev => 
      prev.includes(assetId) ? prev.filter(id => id !== assetId) : [...prev, assetId]
    );
  };

  const handleSelectAllAssets = (currentAssets) => {
    const currentIds = currentAssets.map(asset => asset.id);
    const allSelected = currentIds.every(id => selectedAssetIds.includes(id));
    if (allSelected) {
      setSelectedAssetIds(prev => prev.filter(id => !currentIds.includes(id)));
    } else {
      setSelectedAssetIds(prev => [...new Set([...prev, ...currentIds])]);
    }
  };

  // Open bulk copy dialog
  const openBulkCopyDialog = (type) => {
    setBulkCopyType(type);
    setBulkCopyOpen(true);
  };

  const handleDeleteItem = async () => {
    if (!deleteTarget) return;
    try {
      await inventoryAPI.deleteItem(deleteTarget.id);
      toast.success(`Item "${deleteTarget.unique_id || deleteTarget.item_name}" deleted`);
      setDeleteTarget(null);
      loadItems();
      loadAssets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete item');
      setDeleteTarget(null);
    }
  };

  const handleSuspendItem = async () => {
    if (!suspendTarget) return;
    try {
      const res = await inventoryAPI.suspendItem(suspendTarget.id);
      const newStatus = res.data.status;
      toast.success(`Item "${suspendTarget.unique_id || suspendTarget.item_name}" ${newStatus === 'suspended' ? 'suspended' : 'reactivated'}`);
      setSuspendTarget(null);
      loadItems();
      loadAssets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to suspend/reactivate item');
      setSuspendTarget(null);
    }
  };

  const openLinkProgram = async (item) => {
    setLinkTarget(item);
    setLinkForm({ program_id: '', item_type: 'purchase', quantity: 0, unit_price: 0 });
    try {
      const res = await programsAPI.list();
      setLinkProgramList(Array.isArray(res.data) ? res.data : (res.data?.programs || []));
    } catch {
      toast.error('Failed to load programs');
    }
  };

  const handleLinkProgram = async () => {
    if (!linkTarget) return;
    if (!linkForm.program_id) { toast.error('Pick a program'); return; }
    setLinkSaving(true);
    try {
      await programsAPI.addItem(linkForm.program_id, {
        item_id: linkTarget.id,
        item_name: linkTarget.item_name,
        item_unique_id: linkTarget.unique_id,
        item_type: linkForm.item_type,
        quantity: Number(linkForm.quantity) || 0,
        unit_price: Number(linkForm.unit_price) || 0,
      });
      toast.success(`Linked to ${linkProgramList.find(p => p.id === linkForm.program_id)?.program_name || 'program'}`);
      setLinkTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Link failed');
    } finally {
      setLinkSaving(false);
    }
  };

  // Execute bulk copy
  const handleBulkCopy = async () => {
    const idsTooCopy = bulkCopyType === 'items' ? selectedItemIds : selectedAssetIds;
    if (idsTooCopy.length === 0) return;

    setBulkCopyLoading(true);
    try {
      const res = await inventoryAPI.bulkCopyItems(idsTooCopy);
      toast.success(`Successfully created ${res.data.count} item copies. Edit each to set Unique ID.`);
      setBulkCopyOpen(false);
      
      // Clear selections and reload
      if (bulkCopyType === 'items') {
        setSelectedItemIds([]);
        loadItems();
      } else {
        setSelectedAssetIds([]);
        loadAssets();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to copy items');
    } finally {
      setBulkCopyLoading(false);
    }
  };

  // Get selected items for display in dialog
  const getSelectedItemsForDialog = () => {
    if (bulkCopyType === 'items') {
      return items.filter(item => selectedItemIds.includes(item.id));
    } else {
      return assets.filter(asset => selectedAssetIds.includes(asset.id));
    }
  };

  const itemsTotalPages = Math.ceil(itemsTotal / limit);
  const assetsTotalPages = Math.ceil(assetsTotal / limit);

  return (
    <div className="space-y-4" data-testid="item-master-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight">Item Master</h1>
          <p className="text-xs text-muted-foreground">{itemsTotal + assetsTotal} total items</p>
        </div>
        <div className="flex gap-2">
          {/* Bulk Copy Button - Items */}
          {activeTab === 'items' && selectedItemIds.length > 0 && (
            <Button size="sm" variant="outline" onClick={() => openBulkCopyDialog('items')} data-testid="bulk-copy-items-btn">
              <Files className="w-4 h-4 mr-1" /> Bulk Copy ({selectedItemIds.length})
            </Button>
          )}
          {/* Bulk Copy Button - Assets */}
          {activeTab === 'assets' && selectedAssetIds.length > 0 && (
            <Button size="sm" variant="outline" onClick={() => openBulkCopyDialog('assets')} data-testid="bulk-copy-assets-btn">
              <Files className="w-4 h-4 mr-1" /> Bulk Copy ({selectedAssetIds.length})
            </Button>
          )}
          <input
            ref={importInputRef}
            type="file"
            accept=".xlsx,.xlsm"
            className="hidden"
            onChange={handleImportFile}
            data-testid="import-items-input"
          />
          <Button size="sm" variant="outline" onClick={handleDownloadTemplate} disabled={downloadingTemplate} data-testid="download-template-btn">
            <Download className="w-4 h-4 mr-1" /> {downloadingTemplate ? 'Preparing...' : 'Template'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => importInputRef.current?.click()} disabled={importing} data-testid="import-excel-btn">
            {importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} {importing ? 'Importing...' : 'Import Excel'}
          </Button>
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={handleTidyCategories} disabled={tidying} data-testid="tidy-categories-btn" title="Snap legacy category spellings to standard labels">
              {tidying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wrench className="w-4 h-4 mr-1" />} {tidying ? 'Tidying...' : 'Tidy Categories'}
            </Button>
          )}
          <Button size="sm" onClick={() => navigate('/inventory/items/new')} data-testid="create-item-btn">
            <Plus className="w-4 h-4 mr-1" /> New Item
          </Button>
        </div>
      </div>

      {/* Selection info bar */}
      {(activeTab === 'items' && selectedItemIds.length > 0) && (
        <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
          <span className="text-xs text-blue-700 font-medium">{selectedItemIds.length} item(s) selected</span>
          <Button size="sm" variant="ghost" className="h-6 text-xs text-blue-700" onClick={() => setSelectedItemIds([])}>
            <X className="w-3 h-3 mr-1" /> Clear
          </Button>
        </div>
      )}
      {(activeTab === 'assets' && selectedAssetIds.length > 0) && (
        <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
          <span className="text-xs text-blue-700 font-medium">{selectedAssetIds.length} asset(s) selected</span>
          <Button size="sm" variant="ghost" className="h-6 text-xs text-blue-700" onClick={() => setSelectedAssetIds([])}>
            <X className="w-3 h-3 mr-1" /> Clear
          </Button>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="items" data-testid="tab-items">
            <Package className="w-3.5 h-3.5 mr-1" /> Items ({itemsTotal})
          </TabsTrigger>
          <TabsTrigger value="assets" data-testid="tab-assets">
            <Wrench className="w-3.5 h-3.5 mr-1" /> Fixed Assets ({assetsTotal})
          </TabsTrigger>
        </TabsList>

        {/* Items Tab */}
        <TabsContent value="items" className="mt-4 space-y-3">
          <Card>
            <CardContent className="p-3">
              <form onSubmit={handleItemsSearch} className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input placeholder="Search items..." value={itemsSearch} onChange={e => setItemsSearch(e.target.value)}
                    className="pl-8 h-8 text-xs" data-testid="item-search-input" />
                </div>
                <Select value={itemsTypeFilter || 'all'} onValueChange={v => { setItemsTypeFilter(v === 'all' ? '' : v); setItemsPage(1); }}>
                  <SelectTrigger className="w-44 h-8 text-xs" data-testid="type-filter">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {ITEM_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={itemsMakeOrBuy || 'all'} onValueChange={v => { setItemsMakeOrBuy(v === 'all' ? '' : v); setItemsPage(1); }}>
                  <SelectTrigger className="w-32 h-8 text-xs" data-testid="make-buy-filter">
                    <SelectValue placeholder="Make/Buy" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="make">Make</SelectItem>
                    <SelectItem value="buy">Buy</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="submit" size="sm" variant="secondary" className="h-8" data-testid="search-btn">Search</Button>
                <Button type="button" size="sm" variant={showAdvanced || activeAdvCount > 0 ? 'default' : 'outline'} className="h-8" onClick={() => setShowAdvanced(s => !s)} data-testid="toggle-advanced-filter">
                  <SlidersHorizontal className="w-3.5 h-3.5 mr-1" /> Filters{activeAdvCount > 0 ? ` (${activeAdvCount})` : ''}
                </Button>
              </form>
              {showAdvanced && (
                <div className="mt-3 pt-3 border-t grid grid-cols-1 sm:grid-cols-4 gap-2 items-end" data-testid="advanced-filter-panel">
                  <div>
                    <Label className="text-[11px] text-muted-foreground">External ID (contains)</Label>
                    <Input value={advExternalId} onChange={e => setAdvExternalId(e.target.value)} placeholder="e.g. ABC"
                      className="h-8 text-xs" data-testid="adv-external-id" onKeyDown={e => { if (e.key === 'Enter') applyAdvancedFilter(); }} />
                  </div>
                  <div>
                    <Label className="text-[11px] text-muted-foreground">Category (contains)</Label>
                    <Input value={advCategory} onChange={e => setAdvCategory(e.target.value)} placeholder="e.g. Cut to Size"
                      className="h-8 text-xs" data-testid="adv-category" onKeyDown={e => { if (e.key === 'Enter') applyAdvancedFilter(); }} />
                  </div>
                  <div>
                    <Label className="text-[11px] text-muted-foreground">Inventory Item</Label>
                    <Select value={advInventory || 'all'} onValueChange={v => setAdvInventory(v === 'all' ? '' : v)}>
                      <SelectTrigger className="h-8 text-xs" data-testid="adv-inventory"><SelectValue placeholder="All" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="yes">Yes (tracked)</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" className="h-8 flex-1" onClick={applyAdvancedFilter} data-testid="apply-advanced-filter">Apply</Button>
                    <Button type="button" size="sm" variant="outline" className="h-8" onClick={clearAdvancedFilter} data-testid="clear-advanced-filter">Clear</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="items-table">
                <thead>
                  <ItemTableHeader 
                    items={items}
                    selectedIds={selectedItemIds}
                    onSelectAll={handleSelectAllItems}
                    sortBy={itemsSortBy}
                    sortOrder={itemsSortOrder}
                    onSort={handleSort}
                  />
                </thead>
                <tbody>
                  <ItemTableBody 
                    items={items} 
                    loading={itemsLoading} 
                    navigate={navigate} 
                    selectedIds={selectedItemIds}
                    onSelectItem={handleSelectItem}
                    onDeleteItem={setDeleteTarget}
                    onSuspendItem={setSuspendTarget}
                    onLinkProgram={openLinkProgram}
                    isAdmin={isAdmin}
                    onDuplicateSheet={setDupSheetItem}
                    procurementStatuses={procurementStatuses}
                  />
                </tbody>
              </table>
            </div>
            <Pagination page={itemsPage} totalPages={itemsTotalPages} total={itemsTotal} setPage={setItemsPage} label="items" />
          </Card>
        </TabsContent>

        {/* Fixed Assets Tab */}
        <TabsContent value="assets" className="mt-4 space-y-3">
          <Card>
            <CardContent className="p-3">
              <form onSubmit={handleAssetsSearch} className="flex gap-2">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input placeholder="Search fixed assets..." value={assetsSearch} onChange={e => setAssetsSearch(e.target.value)}
                    className="pl-8 h-8 text-xs" data-testid="asset-search-input" />
                </div>
                <Button type="submit" size="sm" variant="secondary" className="h-8" data-testid="asset-search-btn">Search</Button>
              </form>
            </CardContent>
          </Card>
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="assets-table">
                <thead>
                  <ItemTableHeader 
                    items={assets}
                    selectedIds={selectedAssetIds}
                    onSelectAll={handleSelectAllAssets}
                  />
                </thead>
                <tbody>
                  <ItemTableBody 
                    items={assets} 
                    loading={assetsLoading} 
                    navigate={navigate}
                    selectedIds={selectedAssetIds}
                    onSelectItem={handleSelectAsset}
                    onDeleteItem={setDeleteTarget}
                    onSuspendItem={setSuspendTarget}
                    onLinkProgram={openLinkProgram}
                    isAdmin={isAdmin}
                  />
                </tbody>
              </table>
            </div>
            <Pagination page={assetsPage} totalPages={assetsTotalPages} total={assetsTotal} setPage={setAssetsPage} label="assets" />
          </Card>
        </TabsContent>
      </Tabs>

      {/* Bulk Copy Confirmation Dialog */}
      <Dialog open={bulkCopyOpen} onOpenChange={setBulkCopyOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Files className="w-5 h-5" />
              Bulk Copy {bulkCopyType === 'items' ? 'Items' : 'Assets'}
            </DialogTitle>
            <DialogDescription>
              You are about to create copies of {bulkCopyType === 'items' ? selectedItemIds.length : selectedAssetIds.length} item(s).
              Each copy will have a <strong>blank Unique ID</strong> that you must edit manually.
            </DialogDescription>
          </DialogHeader>
          
          <div className="max-h-48 overflow-y-auto border rounded-md p-2 bg-muted/30">
            <p className="text-xs font-medium text-muted-foreground mb-2">Items to copy:</p>
            <ul className="space-y-1">
              {getSelectedItemsForDialog().map(item => (
                <li key={item.id} className="text-xs flex items-center gap-2">
                  <Badge variant="outline" className="text-[9px]">{item.item_type}</Badge>
                  <span className="font-medium">{item.item_name}</span>
                  {item.unique_id && <span className="text-muted-foreground font-mono">({item.unique_id})</span>}
                </li>
              ))}
            </ul>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBulkCopyOpen(false)} disabled={bulkCopyLoading}>
              Cancel
            </Button>
            <Button onClick={handleBulkCopy} disabled={bulkCopyLoading} data-testid="confirm-bulk-copy-btn">
              {bulkCopyLoading ? 'Copying...' : `Copy ${bulkCopyType === 'items' ? selectedItemIds.length : selectedAssetIds.length} Item(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Item Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-sm">Delete Item</AlertDialogTitle>
            <AlertDialogDescription className="text-xs">
              Are you sure you want to delete <strong>{deleteTarget?.unique_id}</strong> ({deleteTarget?.item_name})?
              This cannot be undone. Items referenced in purchase orders, work orders, or BOMs may cause issues.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-xs">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteItem} className="text-xs bg-red-600 hover:bg-red-700" data-testid="confirm-delete-item">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Suspend/Reactivate Item Confirmation */}
      <AlertDialog open={!!suspendTarget} onOpenChange={() => setSuspendTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-sm flex items-center gap-2">
              {suspendTarget?.status === 'suspended' ? (
                <><CheckCircle className="w-4 h-4 text-green-600" /> Reactivate Item</>
              ) : (
                <><Ban className="w-4 h-4 text-amber-600" /> Suspend Item</>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs">
              {suspendTarget?.status === 'suspended' ? (
                <>
                  Are you sure you want to reactivate <strong>{suspendTarget?.unique_id}</strong> ({suspendTarget?.item_name})?
                  The item will be available again in Work Orders, Purchase Requests, and other operational forms.
                </>
              ) : (
                <>
                  Are you sure you want to suspend <strong>{suspendTarget?.unique_id}</strong> ({suspendTarget?.item_name})?
                  Suspended items will be hidden from Work Orders, Purchase Requests, and other operational forms, but historical data will be retained.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-xs">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleSuspendItem} 
              className={`text-xs ${suspendTarget?.status === 'suspended' ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-600 hover:bg-amber-700'}`}
              data-testid="confirm-suspend-item"
            >
              {suspendTarget?.status === 'suspended' ? 'Reactivate' : 'Suspend'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Link Item to Program */}
      <Dialog open={!!linkTarget} onOpenChange={(o) => !o && setLinkTarget(null)}>
        <DialogContent className="max-w-md" data-testid="link-program-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2">
              <Link2 className="w-4 h-4 text-indigo-600" /> Link Item to Program
            </DialogTitle>
          </DialogHeader>
          {linkTarget && (
            <div className="space-y-3">
              <div className="rounded-md border bg-slate-50 p-2.5 text-[11px]">
                <p><span className="text-muted-foreground">Item:</span> <span className="font-medium">{linkTarget.item_name}</span></p>
                <p className="font-mono text-[10px] mt-0.5 text-muted-foreground">{linkTarget.unique_id || linkTarget.id}</p>
              </div>
              <div>
                <Label className="text-xs">Program *</Label>
                <SearchableSelect
                  options={linkProgramList.map(p => ({ value: p.id, label: `${p.program_number || ''} · ${p.program_name}`.trim() }))}
                  value={linkForm.program_id}
                  onSelect={(v) => setLinkForm(f => ({ ...f, program_id: v }))}
                  placeholder="Pick a program..."
                  triggerClassName="h-8 text-xs"
                  testId="link-program-select"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Type *</Label>
                  <Select value={linkForm.item_type} onValueChange={(v) => setLinkForm(f => ({ ...f, item_type: v }))}>
                    <SelectTrigger className="h-8 text-xs" data-testid="link-item-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="purchase">Purchase</SelectItem>
                      <SelectItem value="sale">Sale</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Qty</Label>
                  <Input type="number" className="h-8 text-xs" value={linkForm.quantity}
                    onChange={(e) => setLinkForm(f => ({ ...f, quantity: e.target.value }))}
                    data-testid="link-quantity" />
                </div>
                <div>
                  <Label className="text-xs">Price</Label>
                  <Input type="number" className="h-8 text-xs" placeholder="Auto" value={linkForm.unit_price}
                    onChange={(e) => setLinkForm(f => ({ ...f, unit_price: e.target.value }))}
                    data-testid="link-price" />
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground">
                Leave quantity / price at 0 to have the program dashboard auto-resolve them from POs and receipts at runtime.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setLinkTarget(null)}>Cancel</Button>
            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700"
              onClick={handleLinkProgram} disabled={linkSaving} data-testid="confirm-link-program">
              {linkSaving ? 'Linking...' : 'Link to Program'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Excel Import Result Dialog */}
      <Dialog open={!!importResult} onOpenChange={(o) => { if (!o) setImportResult(null); }}>
        <DialogContent className="max-w-lg" data-testid="import-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" /> Import Summary
            </DialogTitle>
            <DialogDescription>Results of your Excel item import.</DialogDescription>
          </DialogHeader>
          {importResult && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-md border p-3">
                  <div className="text-2xl font-bold text-emerald-600">{importResult.created}</div>
                  <div className="text-[11px] text-muted-foreground">Created</div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-2xl font-bold text-amber-600">{importResult.skipped}</div>
                  <div className="text-[11px] text-muted-foreground">Skipped (duplicates)</div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-2xl font-bold text-red-600">{importResult.error_count}</div>
                  <div className="text-[11px] text-muted-foreground">Errors</div>
                </div>
              </div>
              {importResult.pending_approval && importResult.created > 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  Imported items are <strong>pending admin approval</strong> because you are not an admin.
                </p>
              )}
              {importResult.errors?.length > 0 && (
                <div className="max-h-48 overflow-y-auto rounded-md border bg-slate-50 p-2 text-xs space-y-1">
                  {importResult.errors.map((e, i) => (
                    <div key={i} className="text-red-700">• {e}</div>
                  ))}
                  {importResult.error_count > importResult.errors.length && (
                    <div className="text-muted-foreground">…and {importResult.error_count - importResult.errors.length} more</div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button size="sm" onClick={() => setImportResult(null)} data-testid="close-import-result">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DuplicateSheetDialog
        item={dupSheetItem}
        onClose={() => setDupSheetItem(null)}
        onDone={() => { setDupSheetItem(null); loadItems(); }}
      />
    </div>
  );
}
